import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';

const studentLinks = [
  { to: '/buyer/dashboard', label: 'navbar.home' },
  { to: '/furniture', label: 'navbar.browse' },
  { to: '/buyer/saved', label: 'navbar.saved' },
  { to: '/seller/dashboard', label: 'seller.nav.myListings' },
  { to: '/buyer/messages', label: 'navbar.messages' },
  { to: '/profile', label: 'navbar.profile' },
];

const StudentNavbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { toggleLanguage } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleButton = (
    <button
      onClick={toggleLanguage}
      className="px-2 py-1 rounded-md text-xs font-medium border border-gray-300 text-gray-600 hover:text-blue-600 hover:border-blue-400"
      title={t('language.switch')}
    >
      {i18n.language === 'zh' ? 'EN' : '中文'}
    </button>
  );

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/buyer/dashboard" className="text-2xl font-bold text-blue-600">MassUniHub</Link>

          <div className="hidden xl:flex items-center gap-3">
            {studentLinks.map(({ to, label }) => (
              <Link key={to} to={to} className="text-sm font-medium text-gray-700 hover:text-blue-600 px-1 py-2">
                {t(label)}
              </Link>
            ))}
            {toggleButton}
            {user && <span className="text-sm text-gray-700">{t('navbar.welcome', { name: user.firstName })}</span>}
            {user && (
              <button
                onClick={handleLogout}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
              >
                {t('navbar.logout')}
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="xl:hidden text-gray-700 hover:text-blue-600 focus:outline-none"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="student-mobile-navigation"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {isMenuOpen && (
          <div id="student-mobile-navigation" role="group" aria-label="Student navigation" className="xl:hidden border-t border-gray-200 px-2 pt-2 pb-3 space-y-1">
            {studentLinks.map(({ to, label }) => (
              <Link key={to} to={to} className="block px-3 py-2 text-gray-700" onClick={() => setIsMenuOpen(false)}>
                {t(label)}
              </Link>
            ))}
            <div className="px-3 py-2">{toggleButton}</div>
            {user && (
              <div className="border-t border-gray-200 pt-3">
                <span className="block px-3 py-2 text-sm text-gray-700">{t('navbar.welcome', { name: user.firstName })}</span>
                <button onClick={handleLogout} className="block w-full px-3 py-2 text-left text-gray-700 hover:text-blue-600">
                  {t('navbar.logout')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default StudentNavbar;
